import * as _ from 'lodash';
import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import type { ProgressEvent } from 'superagent';

import { ARCHIVE_AND_LICENSE_UPLOAD } from '../../../../shared/featureFlags/flagNames';
import { isLinuxFetcher } from '../../../../shared/fetchers';
import { renderPackage, renderRevision } from '../../../../shared/helpers/renderLocator';
import Locator from '../../../../shared/Locator';
import { getSignedUrl, triggerBuilds, uploadFile } from '../../../api/componentUploader';
import { errorMessageFromResponse } from '../../../helpers/errors';
import { isFeatureEnabled } from '../../../helpers/features/featureFlags';
import { post } from '../../../redux/api';
import { nuc, trimString } from '../../../utils';
import Button from '../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../generic/Button/button-constants';
import ButtonGroup from '../../generic/Button/ButtonGroup';
import InlineLoaderButton from '../../generic/Button/InlineLoaderButton';
import ProgressBar from '../../generic/ProgressBar';
import type {
  ManuallyAddedDependency,
  ManuallyAddedDependencyRequest,
  ManuallyAddedDependencyRequestKind,
} from '../../../../typings/api';

import AddDependencyNav from './AddDependencyNav';
import AddLocatorDependency from './AddLocatorDependency';
import AddUploadDependency from './AddUploadDependency';
import AddUserDefinedDependency from './AddUserDefinedDependency';
import type {
  AddDependencyTab,
  ArchiveModalData,
  LocatorModalData,
  ModalDependencyData,
  URLModalData,
  UserDefinedModalData,
} from './types';

type ModalAddDependencyProps = {
  show: boolean;
  onHide: () => void;
  parentLocator: string;
  // is the user adding a `new` dependency, or `replace`-ing an unknown/unresolved one
  mode?: ManuallyAddedDependencyRequestKind;
  // dependency being overwritten
  dependencyToOverwrite?: string;
  // Optional callback to handle reloading the UI after a dependency is added
  onProjectChange?: () => void;
  vendoredPath?: string;
};

// We store the data for each tab so we can persist user input when they switch tabs
type DependencyFormData = {
  create: UserDefinedModalData;
  search: LocatorModalData;
  upload: ArchiveModalData | URLModalData;
};

/**
 * This modal enables users to add or replace dependencies in their projects. They can add an archive,
 * url, locator, or user-defined dependency.
 *
 * @param {string} show - is the Modal being shown in the UI
 * @param {string} onHide - function to be called when the user closes the modal
 * @param {string} parentLocator - locator of the parent revision that the dependency is being added to
 * @param {string} mode - `replace` if user is replacing an existing dep, `new` if they are adding. Defaults to `new`
 * @param {string} dependencyToOverwrite - dependency being overwritten (if mode is `replace`)
 */
const ModalAddDependency = ({
  show,
  onHide,
  parentLocator,
  dependencyToOverwrite,
  mode = 'new',
  onProjectChange,
  vendoredPath,
}: ModalAddDependencyProps) => {
  const getEmptyFormData = (): DependencyFormData => ({
    create: { vendoredPath },
    search: { vendoredPath },
    upload: { vendoredPath },
  });

  // Upload tab only available if FF is on
  const defaultActiveTab = isFeatureEnabled(ARCHIVE_AND_LICENSE_UPLOAD) ? 'upload' : 'search';
  const [activeTab, setActiveTab] = useState<AddDependencyTab>(defaultActiveTab);

  const [dependencyData, setDependencyData] = useState<DependencyFormData>(getEmptyFormData());
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isArchiveUploading, setIsArchiveUploading] = useState(false);
  const [archiveUploadTotalUploaded, setArchiveUploadTotalUploaded] = useState(0);
  const [hasArchiveUploadError, setHasArchiveUploadError] = useState(false);

  // Resets the modal when it is closed
  const clearForm = () => {
    setActiveTab(defaultActiveTab);
    setDependencyData(getEmptyFormData());
    setIsSaveEnabled(false);
    setIsLoading(false);
    setIsArchiveUploading(false);
    setHasArchiveUploadError(false);
  };

  const setError = (dependency: ModalDependencyData, errorValue: string | null) => {
    setDependencyData({
      ...dependencyData,
      [activeTab]: {
        ...dependency,
        // clear out error message on tab if fields are modified
        error: errorValue,
      },
    });
  };

  // clean-up and then hide modal
  const onModalHide = () => {
    clearForm();
    onHide();
  };

  // This function is called on every progress event update during the archive upload
  const onArchiveUploadProgress = () => (progress: ProgressEvent) => {
    setArchiveUploadTotalUploaded(progress.loaded);
  };

  // handle uploading of archive to S3, and creation of build
  const uploadArchive = async (dep: ArchiveModalData): Promise<void> => {
    const packageSpec = dep.name || '';
    const revision = dep.version || '';

    if (!dep.file) {
      return;
    }

    try {
      setHasArchiveUploadError(false);
      setIsArchiveUploading(true);
      // get url to upload archive to
      const uploadUrl = await getSignedUrl(packageSpec, revision);

      // upload to S3/Minio
      await uploadFile(dep.file, uploadUrl, onArchiveUploadProgress());

      // create project + rev + build
      await triggerBuilds(
        {
          archives: [{ packageSpec, revision }],
          // teams are not needed here, as this is a dependency
          selectedTeams: [],
        },
        {
          description: dep.description,
          projectURL: dep.homepage,
          title: packageSpec,
          // add as a dependency
          dependency: true,
          // do NOT run dependency analysis on the archive
          rawLicenseScan: true,
          // When uploading manual dependencies to a project, be sure to rebuild its package cache.
          rebuildPackageCacheForProject: Locator.parsePackageString(parentLocator),
        },
      );
    } catch (err) {
      // capture that an archive upload error has occured so we can show the ProgressBar
      setHasArchiveUploadError(true);
      throw err;
    } finally {
      setIsArchiveUploading(false);
    }
  };

  const onAdd = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const dependencyToAdd = dependencyData[activeTab];
      // clear out error if exists
      setError(dependencyToAdd, null);

      // If archive, upload to S3/Minio and queue build
      if (dependencyToAdd.kind === 'archive') {
        await uploadArchive(dependencyToAdd);
      }

      // don't send file (if archive) or error
      const newDependency = _.omit(dependencyToAdd, 'file', 'error') as ManuallyAddedDependency;
      // Add dependency to graph of `parentLocator`, and create overwrites for future builds
      await post<{ dependencyData: ManuallyAddedDependencyRequest }, void>(`/api/revisions/${nuc(parentLocator)}/dependencies`, {
        dependencyData: (mode === 'new'
          ? {
              kind: 'new',
              newDependency,
            } : {
              kind: 'replace',
              newDependency,
              overwrittenLocator: dependencyToOverwrite as string,
            }),
      });

      // reload the page so the dependencies will show up in the UI
      // If project change is provided, use that. otherwise, reload the page
      if (onProjectChange) {
        onProjectChange();
      } else {
        window.location.reload();
      }
      setIsLoading(false);
    } catch (err) {
      // set error in the current tab
      setError(dependencyData[activeTab], errorMessageFromResponse(err));
      setIsLoading(false);
    }
  };

  // check that all required fields are provided, so we can set the disabled state on the Save button
  const setSaveEnabledStatus = (dep: ModalDependencyData) => {
    switch (dep.kind) {
      case 'archive':
        return setIsSaveEnabled(!!dep.name && !!dep.version && !!dep.file);
      case 'url':
        return setIsSaveEnabled(!!dep.name && !!dep.version && !!dep.url);
      case 'locator':
        if (isLinuxFetcher(dep.fetcher)) {
          return setIsSaveEnabled(
            !!dep.fetcher && !!dep.packageName && !!dep.revision && !!dep.distro && !!dep.distroRelease && !!dep.arch,
          );
        }
        return setIsSaveEnabled(!!dep.fetcher && !!dep.packageName && !!dep.revision);
      case 'user':
        return setIsSaveEnabled(!!dep.name && !!dep.version && !_.isEmpty(dep.licenses));
      default:
        return setIsSaveEnabled(false);
    }
  };

  // change the tab, and check if the submit button should be enabled
  const onNavChange = (tab: AddDependencyTab) => {
    setActiveTab(tab);
    setSaveEnabledStatus(dependencyData[tab]);
  };

  // Update form on every user input change
  const onChange = (dep: ModalDependencyData) => {
    setError(dep, null);
    setSaveEnabledStatus(dep);
  };

  const getAddDependencyContent = (tab: AddDependencyTab) => {
    switch (tab) {
      case 'create':
        return (
          <AddUserDefinedDependency
            onChange={onChange}
            dependencyToAdd={dependencyData[tab]}
            isDisabled={isLoading}
        />
        );
      case 'upload':
        return (
          <AddUploadDependency
            onChange={onChange}
            dependencyToAdd={dependencyData[tab]}
            isDisabled={isLoading}
        />
        );
      case 'search':
      default:
        return (
          <AddLocatorDependency
            onChange={onChange}
            dependencyToAdd={dependencyData[tab]}
            isDisabled={isLoading}
        />
        );
    }
  };

  const locatorToDisplayStr = (loc: string): string => {
    const projectSection = renderPackage(loc);
    const versionSection = trimString(renderRevision(loc), 18);
    return `${projectSection} (${versionSection})`;
  };

  return (
    <Modal className="modal-add-dependency" onHide={onModalHide} animation show={show}>
      <Modal.Header closeButton>
        <Modal.Title>{mode === 'new' ? 'Add' : 'Overwrite'} Dependency</Modal.Title>
        {dependencyToOverwrite &&
          <div className="modal-add-dependency__subtitle">
            <p>
              This action will replace the following dependency in your project:
            </p>
            <span className="modal-add-dependency__subtitle__locator">
              {locatorToDisplayStr(dependencyToOverwrite)}
            </span>
          </div>}
      </Modal.Header>
      <Modal.Body>
        <AddDependencyNav activeTab={activeTab} onNavClick={onNavChange} />
        <div className="modal-add-dependency__content">
          {getAddDependencyContent(activeTab)}
        </div>
        {(isArchiveUploading || hasArchiveUploadError) &&
          <div className="modal-add-dependency__progress">
            <ProgressBar
              hasFailed={hasArchiveUploadError}
              bytesFinished={archiveUploadTotalUploaded}
              bytesTotal={dependencyData[activeTab].kind === 'archive' ? (dependencyData[activeTab]).file?.size ?? 0 : 0}
            />
          </div>}
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type={BUTTON_TYPE.SECONDARY}
            size={BUTTON_SIZE.LARGE}
            isDisabled={isLoading}
            onClick={onModalHide}>
            Close
          </Button>
          <InlineLoaderButton
            type={BUTTON_TYPE.PRIMARY}
            size={BUTTON_SIZE.LARGE}
            isSubmitButton
            isLoading={isLoading}
            isDisabled={!isSaveEnabled || isLoading}
            onClick={onAdd}>
            {mode === 'new' ? 'Add' : 'Overwrite'}
          </InlineLoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalAddDependency;
